/**
 * B7 (E-1.B.1-07): Import History
 *
 * List past migration jobs with status, record counts, error summaries.
 */
import { useState, useEffect } from "react";
import { useTranslation } from "@/i18n";
import { apiFetch } from "@/lib/api-client";
import { cn } from "@/lib/utils";

interface MigrationJob {
  id: string;
  source: string;
  status: string;
  totalRecords: number;
  importedRecords: number;
  failedRecords: number;
  skippedRecords: number;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

const STATUS_COLORS: Record<string, string> = {
  completed: "bg-[var(--color-success)]/10 text-[var(--color-success)]",
  failed: "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
  running: "bg-[var(--color-primary)]/10 text-[var(--color-primary)]",
  pending: "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]",
  mapping: "bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
  importing: "bg-[var(--color-primary)]/10 text-[var(--color-primary)]",
  validating: "bg-[var(--color-secondary)]/10 text-[var(--color-secondary)]",
  rolled_back: "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
};

export default function MigrationHistoryPage() {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState<MigrationJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    apiFetch<{ jobs: MigrationJob[] }>("/platform/migration/jobs")
      .then((res) => setJobs(res.jobs))
      .catch(() => setJobs([]))
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t("migration.history.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("migration.history.description")}</p>
        </div>
      </div>

      {jobs.length === 0 ? (
        <div className="rounded-lg border border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">{t("migration.history.empty")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {jobs.map((job) => (
            <div key={job.id} className="rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", STATUS_COLORS[job.status] ?? "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]")}>
                    {job.status}
                  </span>
                  <span className="text-sm font-medium text-foreground capitalize">{job.source}</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {new Date(job.createdAt).toLocaleDateString()}
                </span>
              </div>
              <div className="mt-2 grid grid-cols-4 gap-4 text-xs">
                <div>
                  <span className="text-muted-foreground">{t("migration.history.total")}: </span>
                  <span className="font-medium">{job.totalRecords.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t("migration.history.imported")}: </span>
                  <span className="font-medium text-[var(--color-success)]">{job.importedRecords.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t("migration.history.skipped")}: </span>
                  <span className="font-medium text-[var(--color-warning)]">{job.skippedRecords}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t("migration.history.failed")}: </span>
                  <span className="font-medium text-[var(--color-destructive)]">{job.failedRecords}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
